import {
	Drawer,
	type DrawerProps,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import type { FC, PropsWithChildren, SyntheticEvent } from "react";
import { useEffect, useRef } from "react";
import { device } from "web_myplann/Utils/device";

export type MyPlannDrawerProps = DrawerProps & {
	title?: string;
	closeButton?: boolean;
	dissableBackdropClick?: boolean;
};

export const MyPlannDrawer: FC<PropsWithChildren<MyPlannDrawerProps>> = ({
	anchor = "bottom",
	title,
	closeButton,
	dissableBackdropClick,
	children,
	onClose,
	open,
	...rest
}) => {
	const { palette } = useTheme();

	const startYRef = useRef<number | null>(null);
	const closedRef = useRef(false);
	const threshold = 60;

	useEffect(() => {
		if (!open) return;
		device.showNavbar(false).catch(() => {});
		return () => {
			device.showNavbar(true).catch(() => {});
		};
	}, [open]);

	const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		startYRef.current =
			e.touches?.[0]?.clientY ?? e.targetTouches?.[0]?.clientY ?? null;
		e.stopPropagation();
	};

	const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		if (anchor === "bottom" && startYRef.current != null) {
			const y =
				e.touches?.[0]?.clientY ?? e.targetTouches?.[0]?.clientY ?? null;
			if (
				y != null &&
				y - startYRef.current > threshold &&
				!closedRef.current
			) {
				closedRef.current = true;
				onClose?.({}, "escapeKeyDown");
			}
		}
		e.stopPropagation();
	};

	const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
		startYRef.current = null;
		closedRef.current = false;
		e.stopPropagation();
	};

	return (
		<Drawer
			anchor={anchor}
			open={open}
			ModalProps={{
				sx: { zIndex: (t) => t.zIndex.drawer + 1 },
				slotProps: { backdrop: { onClick: (e) => e.stopPropagation() } },
			}}
			slotProps={{
				paper: {
					sx: {
						paddingBottom: "min(env(safe-area-inset-bottom, 0px), 10px)",
					},
					onClick: (e: SyntheticEvent) => {
						e.stopPropagation();
					},
				},
			}}
			onClose={(event, reason) => {
				if (reason === "backdropClick" && closeButton) {
					return;
				}
				if (onClose) onClose(event, reason);
			}}
			{...rest}
		>
			<Stack
				pt={1.2}
				pb={0.8}
				px={1.6}
				direction={"row"}
				alignItems={"center"}
				justifyContent={"space-between"}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				sx={{ touchAction: "none" }}
			>
				<Typography
					display="flex"
					alignItems="flex-start"
					variant="deprecatedH3"
				>
					{title}
				</Typography>

				{closeButton && (
					<IconButton
						onClick={(e) => {
							onClose?.(e, "escapeKeyDown");
						}}
						sx={{
							bgcolor: palette.deprecated.grey[200],
						}}
					>
						<X size={16} color={palette.deprecated.grey[600]} />
					</IconButton>
				)}
			</Stack>
			<Stack
				sx={{
					flex: "1 1 auto",
					overflowY: "auto",
				}}
			>
				{children}
			</Stack>
		</Drawer>
	);
};
